import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { WarningAmberRounded } from "@mui/icons-material";
import Etiqueta from "../../components/kit/Etiqueta";
import { tokens } from "../../theme";
import { usePerfil, useUpdateAreasPerfil, useUpdatePerfil } from "../../queries/usePerfil";
import { useNotificacion, useSetFrecuencia } from "../../queries/useTareas";
import { useAreas } from "../../queries/useTemas";
import { AreaInvestigacion } from "../../types/tema";
import { mensajeDeError } from "../../utils/errores";

/** "Mi perfil": el usuario consulta y edita sus datos (email, alias, foto). */
export default function Perfil() {
  const { data, isLoading, isError } = usePerfil();
  const guardar = useUpdatePerfil();
  const inputFoto = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState("");
  const [alias, setAlias] = useState("");
  const [foto, setFoto] = useState<File | null>(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (data) {
      setEmail(data.email);
      setAlias(data.alias);
    }
  }, [data]);

  const enviar = () => guardar.mutate({ email, alias, foto }, { onSuccess: () => { setFoto(null); setOk(true); } });

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Mi perfil
      </Typography>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {isError && <Alert severity="error">No se pudo cargar tu perfil.</Alert>}

      {data && (
        <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar src={foto ? URL.createObjectURL(foto) : data.foto ?? undefined} sx={{ width: 64, height: 64 }}>
                {data.nombre.charAt(0)}
              </Avatar>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{data.nombre}</Typography>
                <Typography variant="body2" color="text.secondary">
                  RUT {data.rut}
                </Typography>
              </Box>
            </Stack>

            {guardar.isError && <Alert severity="error">{mensajeDeError(guardar.error, "No se pudo guardar.")}</Alert>}

            <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth disabled={guardar.isPending} />
            <TextField label="Alias" value={alias} onChange={(e) => setAlias(e.target.value)} fullWidth disabled={guardar.isPending} />

            <Box>
              <input
                ref={inputFoto}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => setFoto(e.target.files?.[0] ?? null)}
              />
              <Button variant="outlined" onClick={() => inputFoto.current?.click()} disabled={guardar.isPending}>
                {foto ? "Cambiar foto seleccionada" : "Subir foto"}
              </Button>
              {foto && (
                <Typography variant="caption" sx={{ ml: 1.5, color: "text.secondary" }}>
                  {foto.name}
                </Typography>
              )}
            </Box>

            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" onClick={enviar} disabled={guardar.isPending}>
                {guardar.isPending ? "Guardando…" : "Guardar cambios"}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {data?.es_docente && <AreasInteres areasIniciales={data.areas} />}

      <Notificaciones />

      <Snackbar open={ok} autoHideDuration={2500} onClose={() => setOk(false)} message="Perfil actualizado" anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
    </Box>
  );
}

/** Áreas de interés del docente (guían la sugerencia de comisiones afines). */
function AreasInteres({ areasIniciales }: { areasIniciales: AreaInvestigacion[] }) {
  const [editar, setEditar] = useState(false);
  const [ok, setOk] = useState(false);

  const vacio = areasIniciales.length === 0;
  const boton = (
    <Button variant="outlined" onClick={() => setEditar(true)}>
      Editar áreas de interés
    </Button>
  );

  return (
    <Paper elevation={0} sx={{ p: 3, mt: 3, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
      {/* Con áreas cargadas, editar es un retoque ocasional y el botón se va a la
          esquina: lo que se viene a ver son las áreas. Sin ninguna, en cambio, poner
          las áreas ES la acción de la tarjeta, así que el botón se queda abajo —donde
          termina de leerse el texto— y el título lleva el mismo aviso que marca en la
          lista de evaluadores a quien no las tiene: sin áreas no entra en la
          optimización por especialidad al armar comisiones. */}
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Box sx={{ flexGrow: 1 }}>
          {/* Flex y no `verticalAlign`: el aviso de la lista de evaluadores se apoya en
              el texto de una celda, y acá el título es un h6 más alto, donde esa misma
              regla lo deja colgando bajo la línea. */}
          <Typography variant="h6" sx={{ mb: 0.5, display: "flex", alignItems: "center", gap: 0.75 }}>
            Áreas de interés
            {vacio && (
              <Tooltip title="Sin áreas no entras en la asignación de comisiones afines a tu especialidad.">
                <WarningAmberRounded sx={{ fontSize: 19, color: tokens.chip.aviso.fg, display: "block" }} />
              </Tooltip>
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Las áreas en las que trabajas. Se usan para asignarte comisiones afines a los temas.
          </Typography>
        </Box>
        {!vacio && boton}
      </Stack>

      {vacio ? (
        <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
          Aún no has indicado áreas.
        </Typography>
      ) : (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {areasIniciales.map((a) => (
            <Etiqueta key={a.id} variante="contorno">
              {a.nombre}
            </Etiqueta>
          ))}
        </Stack>
      )}
      {vacio && boton}

      {editar && (
        <SeleccionarAreasDialog
          areasIniciales={areasIniciales}
          onClose={() => setEditar(false)}
          onGuardado={() => {
            setEditar(false);
            setOk(true);
          }}
        />
      )}
      <Snackbar open={ok} autoHideDuration={2500} onClose={() => setOk(false)} message="Áreas actualizadas" anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
    </Paper>
  );
}

/** Modal para elegir las áreas de interés: lista completa con un toggle por área. */
function SeleccionarAreasDialog({
  areasIniciales,
  onClose,
  onGuardado,
}: {
  areasIniciales: AreaInvestigacion[];
  onClose: () => void;
  onGuardado: () => void;
}) {
  const { data: opciones = [] } = useAreas();
  const guardar = useUpdateAreasPerfil();
  const [sel, setSel] = useState<Set<number>>(() => new Set(areasIniciales.map((a) => a.id)));
  const [q, setQ] = useState("");

  const toggle = (id: number) =>
    setSel((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const filtradas = opciones.filter((a) => a.nombre.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Áreas de interés</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <TextField size="small" fullWidth placeholder="Buscar área…" value={q} onChange={(e) => setQ(e.target.value)} />
          {guardar.isError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {mensajeDeError(guardar.error, "No se pudieron guardar las áreas.")}
            </Alert>
          )}
        </Box>
        <List sx={{ maxHeight: 360, overflowY: "auto", pt: 0 }}>
          {filtradas.map((a) => (
            <ListItem key={a.id} disablePadding>
              <ListItemButton dense onClick={() => toggle(a.id)}>
                <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
                  <Checkbox edge="start" checked={sel.has(a.id)} tabIndex={-1} disableRipple />
                </ListItemIcon>
                <ListItemText primary={a.nombre} />
              </ListItemButton>
            </ListItem>
          ))}
          {filtradas.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
              Sin coincidencias.
            </Typography>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardar.isPending}>
          Cancelar
        </Button>
        <Button variant="contained" disabled={guardar.isPending} onClick={() => guardar.mutate([...sel], { onSuccess: onGuardado })}>
          {guardar.isPending ? "Guardando…" : `Guardar (${sel.size})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Preferencia de frecuencia del correo de tareas pendientes.
 *
 * El envío todavía no está en marcha, así que la sección se muestra como
 * "Próximamente" y el selector va deshabilitado: se ve lo que vendrá, pero no se
 * promete un correo que nadie manda. Para activarla basta quitar el `disabled` y
 * la etiqueta.
 */
function Notificaciones() {
  const { data } = useNotificacion();
  const cambiar = useSetFrecuencia();
  if (!data) return null;
  return (
    <Paper elevation={0} sx={{ p: 3, mt: 3, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="h6">Notificaciones por correo</Typography>
        <Etiqueta variante="contorno">Próximamente</Etiqueta>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Todavía no enviamos estos correos. Cuando la función esté disponible, aquí eliges con qué frecuencia recibes el
        resumen de tus tareas pendientes.
      </Typography>
      <TextField
        select
        size="small"
        label="Frecuencia"
        value={data.frecuencia}
        onChange={(e) => cambiar.mutate(e.target.value)}
        sx={{ minWidth: 220 }}
        disabled
      >
        {data.opciones.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
    </Paper>
  );
}
